import {
  OrderCreateCargo,
  OrderCreateCargoInfo,
  OrderCreateCargoTotal,
  OrderCreateOperationItem,
  OrderCreatePartner,
  OrderCreatePoint,
  OrderCreateRequest,
  OrderDeliveryInfoDepartureRequest,
  OrderDeliveryInfoDestinationRequest,
  OrderDeliveryInfoRequest
} from 'schema'
import { HECTO, KILO } from 'src/constants/metricPrefixes'
import { div, multiply } from 'src/shared/helpers/number'
import { OrderAddStore } from 'src/store/orderAdd/types'

import { OrderCreateOperationForm } from '../components/OrderCreatePointOperation/types'
import { VOLUME_LIMIT, WEIGHT_LIMIT } from '../components/OrderGuestCreateForm/constants'
import {
  RadioSwitchValue,
  TOrderCreateCargoForm,
  TOrderCreateFormData,
  TOrderCreatePayload,
  TOrderCreatePoint
} from '../types/orderCreateTypes'

export const isOutLimitCargoTotal = (value: OrderCreateCargoTotal) => {
  const { volume, weight } = value

  return volume > VOLUME_LIMIT || weight > WEIGHT_LIMIT
}

const convertUnit = (value = 0, ratio: number) => Number(multiply(value, ratio))

export const prepareCargo = (cargo?: TOrderCreateCargoForm[]) =>
  (cargo || []).reduce(
    (acc, cargoItem) => {
      const {
        palletLength,
        palletWidth,
        palletHeight,
        palletWeight,
        customLength,
        customWidth,
        customHeight,
        customWeight,
        ...item
      } = cargoItem

      if (cargoItem.type === OrderCreateCargo.type.PALLET) {
        const weight = convertUnit(palletWeight, KILO)

        return [
          ...acc,
          {
            ...item,
            length: palletLength,
            width: palletWidth,
            height: palletHeight,
            weight: weight || item.weight
          }
        ]
      }

      if (cargoItem.type === OrderCreateCargo.type.CUSTOM) {
        const weight = convertUnit(customWeight, KILO)

        return [
          ...acc,
          {
            ...item,
            length: customLength,
            width: customWidth,
            height: customHeight,
            weight: weight || item.weight
          }
        ]
      }

      return [...acc, cargoItem]
    },
    [] as unknown as OrderCreateCargo[]
  )

export const prepareCargoTotal = (cargo?: OrderCreateCargoTotal) => {
  const maxWeight = convertUnit(cargo?.maxWeight, KILO)
  const weight = convertUnit(cargo?.weight, KILO)
  const length = convertUnit(cargo?.length, HECTO)
  const width = convertUnit(cargo?.width, HECTO)
  const height = convertUnit(cargo?.height, HECTO)
  const defaultCargoTotal = { ...cargo, type: OrderCreateCargoTotal.type.CUSTOM }
  const cargoTotal = (
    cargo?.maxWeight ? { ...defaultCargoTotal, maxWeight } : defaultCargoTotal
  ) as OrderCreateCargoTotal

  if (weight) {
    return {
      ...cargoTotal,
      weight,
      length,
      width,
      height
    } as OrderCreateCargoTotal
  }

  return cargoTotal
}

export const splitOperationsByTerminal = (
  operations: OrderCreateOperationForm[],
  departureTerminalId?: string,
  destinationTerminalId?: string
) => {
  const toOperationItem = ({ operationId, amount }: OrderCreateOperationForm): OrderCreateOperationItem => ({
    operationId,
    amount
  })
  const commonOperations: OrderCreateOperationItem[] = []
  const departureOperations: OrderCreateOperationItem[] = []
  const destinationOperations: OrderCreateOperationItem[] = []

  operations.forEach((op) => {
    if (!op.terminalId) {
      commonOperations.push(toOperationItem(op))

      return
    }

    if (op.pointType === 'departure') {
      if (op.terminalId === departureTerminalId) {
        departureOperations.push(toOperationItem(op))
      }

      return
    }

    if (op.pointType === 'destination') {
      if (op.terminalId === destinationTerminalId) {
        destinationOperations.push(toOperationItem(op))
      }

      return
    }

    if (op.terminalId === departureTerminalId) {
      departureOperations.push(toOperationItem(op))
    }

    if (op.terminalId === destinationTerminalId) {
      destinationOperations.push(toOperationItem(op))
    }
  })

  return {
    operation: commonOperations,
    departure: departureOperations,
    destination: destinationOperations
  }
}

const toOrderCreatePoint = ({ terminalId, isCourier, addressDetail }: TOrderCreatePoint): OrderCreatePoint => ({
  terminalId,
  isCourier,
  ...(addressDetail ? { addressDetail } : {})
})

export const prepareOrderCreateRequest = (formData: TOrderCreateFormData): TOrderCreatePayload => {
  const {
    cargo = [],
    cargoTotal,
    cargoType,
    comment,
    customer,
    departureDate,
    departurePartner,
    departurePoint,
    destinationPartner,
    destinationPoint,
    isAgreeOffer,
    operation,
    payer
  } = formData
  const data: Partial<TOrderCreatePayload> = { isAgreeOffer: Boolean(isAgreeOffer) }

  if (comment) {
    data.comment = comment
  }

  if (payer?.role) {
    data.payer = { role: payer.role }
  }

  if (customer) {
    const { type, customerId } = customer

    data.customer = customerId
      ? {
          type,
          customerId
        }
      : {
          type
        }
  }

  if (cargoType === OrderCreateRequest.cargoType.SEPARATE) {
    data.cargoInfo = {
      type: OrderCreateCargoInfo.type.SEPARATE,
      separate: prepareCargo(cargo)
    }
  } else {
    data.cargoInfo = {
      type: OrderCreateCargoInfo.type.TOTAL,
      total: prepareCargoTotal(cargoTotal)
    }
  }

  const departure: NonNullable<TOrderCreatePayload['departure']> = {}
  const destination: NonNullable<TOrderCreatePayload['destination']> = {}

  if (departurePoint) {
    departure.point = toOrderCreatePoint(departurePoint)
  }

  if (destinationPoint) {
    destination.point = toOrderCreatePoint(destinationPoint)
  }

  if (departurePartner) {
    const { companyId, contactId, type, isUser } = departurePartner

    departure.partner = {
      companyId,
      contactId,
      type,
      isUser: isUser === RadioSwitchValue.yes
    } as unknown as OrderCreatePartner
  }

  if (destinationPartner) {
    const { companyId, contactId, type, isUser } = destinationPartner

    destination.partner = {
      companyId,
      contactId,
      type,
      isUser: isUser === RadioSwitchValue.yes
    } as unknown as OrderCreatePartner
  }

  if (departureDate) {
    departure.departureDate = departureDate
  }

  if (operation?.length) {
    const {
      operation: commonOperations,
      departure: departureOperations,
      destination: destinationOperations
    } = splitOperationsByTerminal(operation, departurePoint?.terminalId, destinationPoint?.terminalId)

    if (commonOperations.length) {
      data.operations = commonOperations
    }

    if (departureOperations.length) {
      departure.operation = departureOperations
    }

    if (destinationOperations.length) {
      destination.operation = destinationOperations
    }
  }

  if (Object.keys(departure).length) {
    data.departure = departure
  }

  if (Object.keys(destination).length) {
    data.destination = destination
  }

  return data as TOrderCreatePayload
}

export type DeliveryInfoOperations = Partial<{
  departure: Pick<OrderDeliveryInfoDepartureRequest, 'operation'>
  destination: Pick<OrderDeliveryInfoDestinationRequest, 'operation'>
  operation: OrderDeliveryInfoRequest['operation']
}>

export const prepareOperationsForDeliveryInfo = (
  operations: OrderCreateOperationForm[],
  departureTerminalId: string,
  destinationTerminalId: string
): DeliveryInfoOperations => {
  const { operation, departure, destination } = splitOperationsByTerminal(
    operations,
    departureTerminalId,
    destinationTerminalId
  )
  const result: DeliveryInfoOperations = {}

  if (operation.length) {
    result.operation = operation
  }

  if (departure.length) {
    result.departure = { operation: departure }
  }

  if (destination.length) {
    result.destination = { operation: destination }
  }

  return result
}

export const prepareCargoField = (cargo: Partial<OrderCreateCargo>) => ({
  amount: cargo.amount,
  type: cargo?.type,
  [`${cargo?.type}Weight`]: div(cargo?.weight || 0, KILO),
  [`${cargo?.type}Length`]: cargo?.length,
  [`${cargo?.type}Width`]: cargo?.width,
  [`${cargo?.type}Height`]: cargo?.height
})

export const decode = <T extends { [key: string]: any }>(s: string): T => JSON.parse(decodeURI(window.atob(s)))

export const getPointPayload = ({
  departurePoint,
  destinationPoint
}: {
  departurePoint?: OrderCreatePoint
  destinationPoint?: OrderCreatePoint
}): OrderAddStore => ({
  // @ts-ignore
  fromPoint: departurePoint
    ? {
        selectedOption: { ...departurePoint.addressDetail, value: departurePoint.addressDetail?.address || '' },
        selectedTerminal: null,
        daDataSearchAddressValue: departurePoint.addressDetail?.address || '',
        isAddressCourierDelivery: true,
        isAddressTerminalDelivery: true,
        isCourier: undefined,
        terminals: null,
        comment: '',
        errorAddress: ''
      }
    : {},
  // @ts-ignore
  toPoint: destinationPoint
    ? {
        selectedOption: { ...destinationPoint.addressDetail, value: destinationPoint.addressDetail?.address || '' },
        selectedTerminal: null,
        daDataSearchAddressValue: destinationPoint.addressDetail?.address || '',
        isAddressCourierDelivery: true,
        isAddressTerminalDelivery: true,
        isCourier: undefined,
        terminals: null,
        comment: '',
        errorAddress: ''
      }
    : {}
})
